import React, { memo, useCallback, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import type { CorkBoardAttachment } from '@/types/residents/corkBoard';
import { InlineImageGallery } from './InlineImageGallery';
import { AttachmentPreview } from './AttachmentPreview';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface ThreadBodyProps {
  body: string;
  attachments: CorkBoardAttachment[];
  onImagePress?: (imageUrls: string[], index: number) => void;
  onDocumentPress?: (url: string, title: string) => void;
}

const isImage = (a: CorkBoardAttachment) => !!a.fileType && a.fileType.startsWith('image/');

export const ThreadBody: React.FC<ThreadBodyProps> = memo(function ThreadBody({ body, attachments, onImagePress, onDocumentPress }) {
  const { colors } = useThemeColors();
  const imageAttachments = useMemo(() => attachments.filter(isImage), [attachments]);
  const docAttachments = useMemo(() => attachments.filter((a) => !isImage(a)), [attachments]);
  const imageUrls = useMemo(() => imageAttachments.map((a) => a.fileUrl), [imageAttachments]);

  // InlineImageGallery requires a non-optional handler; fall back to noop when
  // the parent has not provided one so the gallery still renders read-only.
  const handleImagePress = useCallback(
    (urls: string[], index: number) => {
      onImagePress?.(urls, index);
    },
    [onImagePress],
  );

  return (
    <View style={styles.container}>
      <Text style={[styles.body, { color: colors.text.primary }]}>{body}</Text>
      {imageUrls.length > 0 && (
        <InlineImageGallery imageUrls={imageUrls} onImagePress={handleImagePress} />
      )}
      {docAttachments.length > 0 && (
        <AttachmentPreview
          attachments={docAttachments}
          onImagePress={onImagePress}
          onDocumentPress={onDocumentPress}
        />
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  body: {
    ...REDESIGN_TYPOGRAPHY.body,
    fontSize: 15,
    lineHeight: 24,
  },
});
